import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'
import { Button, Icon, Loader } from 'foundations-library/components'
import RenderEmptyState from 'empty-states'
import { UserContext } from 'providers'
import { NavigationContext } from 'navigation/NavigationContext'
import {
    SmsNumberCampaignContext,
    SmsNumberCampaignContextProvider
} from '../../../../sms-enrollment/src/contexts/SmsNumberCampaignContext'
import WatchDemoDialog from '../../../../sms-enrollment/src/components/WatchDemoDialog'
import { getValue } from 'remote-config-value'
import PhoneComUser from 'phone-com-user'

const styles = (theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%'
    },
    conversationCustom: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        maxWidth: 528,
        minHeight: 36,
        padding: '6px 10px',
        boxSizing: 'border-box',
        background: 'linear-gradient(78.05deg, #AADBA0 9.2%, #D7EBD3 48.86%, #AADBA0 91.7%)',
        borderRadius: 5
    },
    conversationCustomIcon: {
        display: 'flex'
    },
    conversationCustomText: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: 13,
        lineHeight: '21px',
        letterSpacing: 0
    },
    learnMoreLink: {
        color: theme['foundations-library'].colors.primary[500],
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:visited': {
            textDecoration: 'underline',
            color: theme['foundations-library'].colors.primary[500]
        }
    }
})

const CustomContentView = ({ classes }) => (
    <div className={classes.conversationCustom}>
        <span className={classes.conversationCustomIcon} aria-hidden='true'>
            <Icon name='infotip-sm' size={24} color='black' />
        </span>
        <span className={classes.conversationCustomText}>
            Contact your admin to get started with texting.
        </span>
    </div>
)

CustomContentView.propTypes = {
    classes: PropTypes.object.isRequired
}

const getSMSActivationPromptContent = ({
    classes,
    onWatchDemoClick,
    onStartRegistrationClick
}) => ({
    title: 'Activate Business SMS',
    description:
        'To send and receive texts under standard carrier guidelines, verify your business profile in a few simple steps. This one-time setup protects your messaging reputation and ensures your texts bypass spam filters.',
    customContent: (
        <div>
            Toll-Free numbers are exempt. <a
                                        href={getValue('sms_registration_brands_learn_more_link')}
                                        target="_blank"
                                        className={classes.learnMoreLink}
                                        data-testid='landing-page-learn-more'
                                    >
                                        Learn more
                                    </a>
        </div>
    ),
    buttons: [
        <Button
            key='watch-demo'
            variant='outlined'
            color='neutral'
            iconLeft
            icon='play-outline'
            size='large'
            onClick={onWatchDemoClick}
        >
            Watch a demo video
        </Button>,
        <Button
            key='start-registration'
            variant='contained'
            color='primary'
            iconLeft
            icon='add'
            size='large'
            onClick={onStartRegistrationClick}
        >
            Start SMS Registration
        </Button>
    ]
})

const getSMSBlockedContent = (classes) => ({
    title: 'Texts aren’t enabled for your account',
    description: '',
    customContent: <CustomContentView classes={classes} />,
    buttons: [
        <Button
            key='start-chat'
            variant='contained'
            color='neutral'
            disabled
            size='large'
            icon='go'
        >
            Let’s start a new chat
        </Button>
    ]
})

const getStartConversationContent = (onStartConversationClick) => ({
    title: 'Start a new conversation',
    description: 'Type your first message below',
    buttons: [
        <Button
            key='start-chat'
            variant='contained'
            color='primary'
            icon='go'
            size='large'
            onClick={onStartConversationClick}
        >
            Let’s start a new chat
        </Button>
    ]
})

const EmptyState = ({ classes, content }) => (
    <div className={classes.root}>
        <RenderEmptyState
            title={{ text: content.title }}
            description={{ text: content.description }}
            svg={{ name: 'messages-content' }}
            customContent={content.customContent}
            buttons={content.buttons}
        />
    </div>
)

EmptyState.propTypes = {
    classes: PropTypes.object.isRequired,
    content: PropTypes.object.isRequired
}

const NoConversationUserView = ({ classes, onStartConversationClick, isSMSRegistrationCompleted}) => {
    const content = isSMSRegistrationCompleted
        ? getStartConversationContent(onStartConversationClick)
        : getSMSBlockedContent(classes)

    return <EmptyState classes={classes} content={content} />
}

NoConversationUserView.propTypes = {
    isSMSRegistrationCompleted: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    onStartConversationClick: PropTypes.func.isRequired
}

const NoConversationAdminView = ({
    classes,
    onStartConversationClick,
    onStartRegistrationClick,
}) => {
    const [showDemoModal, setShowDemoModal] = useState(false)
    const { campaignsWithNumber, loadingSmsNumberCampaignContext } = useContext(SmsNumberCampaignContext)
    const navigationContext = useContext(NavigationContext)
    const isSMSRegistrationCompleted = campaignsWithNumber.length > 0
    if(loadingSmsNumberCampaignContext) 
    {
        return (<div className={classes.root}><Loader size={40} data-testid='dialog-loader' /></div>)
    }

    const content = isSMSRegistrationCompleted
        ? getStartConversationContent(onStartConversationClick)
        : getSMSActivationPromptContent({
            classes,
            onWatchDemoClick: () => setShowDemoModal(true),
            onStartRegistrationClick: () => {
                const path = navigationContext?.getPathToNavItem('sms-registration')
                if (path) {
                    onStartRegistrationClick(path)
                }
            }
        })
        
    return (
        <>
            <EmptyState classes={classes} content={content} />
            <WatchDemoDialog
                isOpen={showDemoModal}
                onClose={() => setShowDemoModal(false)}
            />
        </>
    )
}

NoConversationAdminView.propTypes = {
    classes: PropTypes.object.isRequired,
    onStartConversationClick: PropTypes.func.isRequired,
    onStartRegistrationClick: PropTypes.func.isRequired
}

const NoConversationView = ({
    extension,
    classes,
    onStartConversationClick,
    onStartRegistrationClick
}) => {
    const isAdmin = PhoneComUser.getRole() === 'account'
    const hasRegisteredNumbers = Boolean(Object.keys(extension?.phone_number).length)
    const isSMSRegistrationCompleted = hasRegisteredNumbers

    if (isAdmin) {
        return (
            <SmsNumberCampaignContextProvider>
                <NoConversationAdminView
                    classes={classes}
                    onStartConversationClick={onStartConversationClick}
                    onStartRegistrationClick={onStartRegistrationClick}
                />
            </SmsNumberCampaignContextProvider>
        )
    }
    return (
        <NoConversationUserView
            isSMSRegistrationCompleted={isSMSRegistrationCompleted}
            classes={classes}
            onStartConversationClick={onStartConversationClick}
        />
    )
}

NoConversationView.propTypes = {
    classes: PropTypes.object.isRequired,
    onStartConversationClick: PropTypes.func.isRequired,
    onStartRegistrationClick: PropTypes.func.isRequired,
    extension: PropTypes.object.isRequired
}

export default withStyles(styles)(NoConversationView)
